import { AlertCircle, Instagram, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AutoDmService from "../services/autoDmService";
import useToastStore from "../store/useToastStore";

const stripInstagramHash = (value: string | null) =>
  value?.replace(/#_$/, "") ?? "";

const InstagramAuthCallbackPage = () => {
  const { showToast } = useToastStore();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const authResult = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const code = stripInstagramHash(params.get("code"));
    const error = params.get("error");
    const errorReason = params.get("error_reason");
    const errorDescription = params.get("error_description");
    const state = params.get("state");
    const savedState = sessionStorage.getItem("instagram_oauth_state");
    const stateMatches = !state || !savedState || state === savedState;

    return { code, error, errorReason, errorDescription, stateMatches };
  }, []);

  // Verify the token in the background and take the user straight back to the
  // Auto-DM page on success — no technical details, no manual steps.
  useEffect(() => {
    if (authResult.error) {
      setErrorMessage(
        authResult.errorDescription || authResult.errorReason || authResult.error
      );
      return;
    }
    if (!authResult.stateMatches) {
      setErrorMessage(
        "This connection link has expired. Please start the Instagram connection again."
      );
      return;
    }
    if (!authResult.code) {
      navigate("/auto-dm", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await AutoDmService.handleInstagramCallback({
          code: authResult.code,
        });
        if (cancelled) return;
        const message =
          typeof response?.message === "string"
            ? response.message
            : "Instagram connected successfully 🎉";
        showToast(message, "success");
        navigate("/auto-dm", { replace: true });
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "We couldn't connect your Instagram account. Please try again."
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authResult, navigate, showToast]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-50 to-rose-50 flex items-center justify-center text-pink-600 mb-5">
          <Instagram className="w-7 h-7" />
        </div>

        {errorMessage ? (
          <>
            <div className="mx-auto mb-3 flex items-center justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">
              Connection failed
            </h1>
            <p className="mt-2 text-sm text-gray-500">{errorMessage}</p>
            <Link
              to="/auto-dm"
              className="mt-6 inline-flex w-full items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              Back to Auto-DM
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-gray-900">
              Connecting your Instagram…
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Hang tight, this only takes a moment. We'll take you back
              automatically.
            </p>
            <div className="mt-6 flex items-center justify-center text-blue-600">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstagramAuthCallbackPage;
